import { useState } from "react"
import { Button } from "@/components/ui/button"
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog"
import { Textarea } from "@/components/ui/textarea"
import { Label } from "@/components/ui/label"
import { Input } from "@/components/ui/input"
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select"
import { useAuth } from "@/hooks/useAuth"
import { useCreateBroadcastMessage } from "@/hooks/useBroadcast"
import { Users, Send } from "lucide-react"

interface BroadcastModalProps {
  open: boolean
  onOpenChange: (open: boolean) => void
}

export const BroadcastModal = ({ open, onOpenChange }: BroadcastModalProps) => {
  const [title, setTitle] = useState("")
  const [message, setMessage] = useState("")
  const [targetRole, setTargetRole] = useState<string | undefined>(undefined)
  const { profile } = useAuth()
  const createBroadcastMutation = useCreateBroadcastMessage()

  const isSindico = profile?.role === 'sindico'
  const isPorteiro = profile?.role === 'porteiro'

  const handleSendBroadcast = async () => {
    if (!title.trim() || !message.trim()) {
      return
    }

    try {
      await createBroadcastMutation.mutateAsync({
        title: title.trim(),
        content: message.trim(),
        targetRole
      })
      
      setTitle("")
      setMessage("")
      setTargetRole(undefined)
      onOpenChange(false)
    } catch (error) {
      console.error("Erro no broadcast:", error)
    }
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Users className="h-5 w-5" />
            Enviar Comunicado
          </DialogTitle>
        </DialogHeader>
        
        <div className="space-y-4">
          <div>
            <Label htmlFor="broadcast-title">Título</Label>
            <Input
              id="broadcast-title"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              placeholder="Título do comunicado..."
              className="mt-2"
              disabled={createBroadcastMutation.isPending}
            />
          </div>

          <div>
            <Label htmlFor="broadcast-message">Mensagem</Label>
            <Textarea
              id="broadcast-message"
              value={message}
              onChange={(e) => setMessage(e.target.value)}
              placeholder="Digite o comunicado que será enviado..."
              className="min-h-[120px] mt-2"
              disabled={createBroadcastMutation.isPending}
            />
          </div>
          
          {isSindico && (
            <div>
              <Label htmlFor="target-role">Destinatários</Label>
              <Select 
                value={targetRole} 
                onValueChange={setTargetRole}
                disabled={createBroadcastMutation.isPending}
              >
                <SelectTrigger id="target-role" className="mt-2">
                  <SelectValue placeholder="Todos" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value={undefined}>Todos</SelectItem>
                  <SelectItem value="morador">Apenas Moradores</SelectItem>
                  <SelectItem value="porteiro">Apenas Porteiros</SelectItem>
                </SelectContent>
              </Select>
            </div>
          )}
          
          <div className="bg-muted p-3 rounded-lg">
            <p className="text-sm text-muted-foreground">
              <strong>Comunicado de:</strong> {isSindico ? 'Síndico' : isPorteiro ? 'Portaria' : profile?.role}
            </p>
            <p className="text-xs text-muted-foreground mt-1">
              {isSindico 
                ? "Como síndico, você pode enviar comunicados para todos ou grupos específicos." 
                : isPorteiro 
                  ? "Como porteiro, você pode enviar comunicados para todos os moradores."
                  : "Este comunicado será enviado conforme suas permissões."}
            </p>
          </div>
          
          <div className="flex gap-2 justify-end">
            <Button
              variant="outline"
              onClick={() => onOpenChange(false)}
              disabled={createBroadcastMutation.isPending}
            >
              Cancelar
            </Button>
            <Button
              onClick={handleSendBroadcast}
              disabled={!title.trim() || !message.trim() || createBroadcastMutation.isPending}
            >
              {createBroadcastMutation.isPending ? (
                "Enviando..."
              ) : (
                <>
                  <Send className="h-4 w-4 mr-2" />
                  Enviar Comunicado
                </>
              )}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  )
}